// Plan 24c Phase B, Task 2 — Toast Stack overlay manager.
// A persistent overlay that floats ABOVE the active screen and survives
// screen transitions. Wraps the createToastStack primitive from
// src/client/ui/components/toast.ts.

import { createToastStack } from '../components/toast';
import type { ToastOpts } from '../components/toast';
import type { GsapRegistry } from '../../renderer/vfx/gsap-registry';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export interface ToastStackOverlayOpts {
  /** DOM container the overlay mounts into. Same host that boot.ts uses. */
  host: HTMLElement;
  gsapRegistry: GsapRegistry;
  /** Optional override for vitest. Default: 3. */
  maxVisible?: number;
  /** Auto-dismiss delay in milliseconds. Default: 4000 from primitive. */
  dismissMs?: number;
}

export interface ToastStackOverlayHandle {
  /** Push a new toast into the stack. Oldest toast is auto-removed when max is reached. */
  push(opts: ToastOpts): void;
  /** Dispose the entire overlay + all toasts. Idempotent. */
  dispose(): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createToastStackOverlay(opts: ToastStackOverlayOpts): ToastStackOverlayHandle {
  const { host, gsapRegistry, maxVisible = 3, dismissMs } = opts;

  let disposed = false;

  // ── Build wrapper root element ────────────────────────────────────────────
  // The inner createToastStack sets its own position:fixed and z-index.
  // The wrapper is a plain container that gives us a removable DOM anchor.

  const root = document.createElement('div');
  root.setAttribute('data-overlay', 'toast-stack');
  host.appendChild(root);

  // ── Delegate to the inner primitive ──────────────────────────────────────

  const innerStack = createToastStack({
    gsapRegistry,
    mount: root,
    maxVisible,
    dismissMs,
  });

  // ── Public handle ─────────────────────────────────────────────────────────

  function push(toastOpts: ToastOpts): void {
    if (disposed) return;
    innerStack.push(toastOpts);
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    innerStack.dispose();

    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  return { push, dispose };
}
